import type { Middleware } from 'openapi-fetch'

/**
 * Tracks one in-flight GET per unique `METHOD URL` key (URL already includes
 * the serialized query string by the time `onRequest` sees it, so
 * `GET /rfq` and `GET /rfq?workspaceId=acct-1` are different keys — see
 * `apps/web/src/verticals/workspaces/useWorkspaceRfqs.ts`). Entries are
 * removed the instant the request settles (`onResponse`/`onError`) — there is
 * deliberately no TTL and nothing here ever survives past that point, so a
 * later identical GET always hits the network fresh. This is an in-flight
 * dedupe only, NOT a cache.
 */
interface PendingGet {
  /** Resolves to a clone of the fully middleware-processed response, exactly once, for every follower waiting on this key. */
  promise: Promise<Response>
  resolve: (response: Response) => void
  reject: (error: unknown) => void
  /** See `safetyTimeoutMs` on the factory — cleared on a normal settle, fires only to recover a leaked entry. */
  timer: ReturnType<typeof setTimeout>
}

/**
 * Sentinel rejection reason used ONLY by the safety-timeout recovery path
 * below — never a real request failure. A follower checks for this exact
 * reference to tell "the leader genuinely failed, propagate that" apart from
 * "the leader's entry leaked, stop waiting and make a real fetch instead."
 */
const SAFETY_TIMEOUT = Symbol('dedupeGetMiddleware: pending entry safety timeout')

/** Default for `createDedupeGetMiddleware`'s `safetyTimeoutMs` — see its doc comment. */
const DEFAULT_SAFETY_TIMEOUT_MS = 30_000

/**
 * Builds a `dedupeGetMiddleware` instance with its own isolated in-flight
 * map — a factory (rather than one fixed singleton) so tests can construct
 * an instance with a short `safetyTimeoutMs` without waiting out the real
 * default, and without leaking state into other tests via a shared module
 * -level map. Production code should use the pre-built `dedupeGetMiddleware`
 * export below; there is no need for more than one instance in the app.
 *
 * Collapses concurrent, byte-identical in-flight GET requests into a single
 * network call (UID-589 task 2) — e.g. `useHydrateRfqCount` (mounted globally
 * in `AppShell`) and `<OpenQuotes>` both firing a bare `GET /rfq` in the same
 * tick on `/`, or `Sidebar`'s mount-time `refetchUploadSessions()` re-firing
 * `useUploadSessions`'s own `GET /uploads/sessions` effect.
 *
 * Only GET is ever deduped — POST/PATCH/DELETE etc. always hit the network,
 * since they are not safe to share.
 *
 * MUST be registered on `apiClient` (`client.ts`) BEFORE `authMiddleware` and
 * `nonJsonResponseMiddleware`. openapi-fetch runs `onResponse` in
 * reverse-registration order, so registering this middleware first means its
 * `onResponse` runs LAST — after `nonJsonResponseMiddleware` has already
 * sanitized a non-JSON body and after `authMiddleware` has already run its
 * 401-session-expiry check. The response this middleware then shares with
 * followers is that fully-processed response, not the raw network response.
 *
 * This matters because of how openapi-fetch's core `coreFetch` is written:
 * when ANY middleware's `onRequest` returns a `Response` (our move for a
 * follower — see below), `coreFetch` short-circuits straight past the actual
 * `fetch()` call AND past the entire `onResponse`/`onError` middleware chain
 * for that call. A follower therefore never re-runs `authMiddleware` or
 * `nonJsonResponseMiddleware` itself — which is exactly why it must receive a
 * response that already went through them, sourced from the leader's own
 * pass through the full chain, rather than a raw one.
 *
 * Mechanics:
 * - The first caller for a key (the "leader") registers a pending entry
 *   (synchronously, before any `await`, so a same-tick concurrent caller
 *   always observes it) and proceeds through the real `fetch()` + the rest of
 *   the middleware chain untouched.
 * - Any other caller for the same key (a "follower") awaits the leader's
 *   entry and returns a `.clone()` of it from `onRequest`, short-circuiting
 *   its own network call. Response bodies can only be read once, so every
 *   follower gets its own clone, and the shared "final" response itself is
 *   never read directly (only ever cloned), so any number of followers can
 *   clone it safely.
 * - When the leader's response finishes the middleware chain (`onResponse`),
 *   the pending entry is deleted and resolved with `response.clone()` — the
 *   leader keeps the original to read itself, and followers share the clone.
 * - If the leader's underlying `fetch()` throws (network failure, not an
 *   HTTP error status), `onError` deletes the pending entry and rejects it,
 *   so every waiting follower's call also rejects — the same class of
 *   failure they would have seen making the request themselves. The leader's
 *   own error is never swallowed here; this only ever forwards it.
 *
 * Leaked-entry recovery (openapi-fetch's `coreFetch`, verified against
 * `node_modules/openapi-fetch/src/index.js`): the `try/catch` around the
 * fetch call there wraps ONLY `await fetch(...)` — the `onResponse` loop that
 * runs afterwards sits OUTSIDE it. `onError` therefore only fires when
 * `fetch()` itself throws; if a middleware's `onResponse` throws instead
 * (e.g. `nonJsonResponseMiddleware` or `authMiddleware`, both of which run
 * BEFORE this middleware's `onResponse` per the registration order above),
 * neither this middleware's `onResponse` nor its `onError` ever runs, so the
 * pending entry would otherwise never be deleted or settled — every future
 * identical GET would await a promise that never resolves, hanging that
 * endpoint for the rest of the session. `safetyTimeoutMs` (default 30s,
 * comfortably above any real request's expected duration — recall task 1 of
 * UID-589 was about requests taking 12-14s) bounds that: it always starts
 * when the leader registers, regardless of what happens afterwards, and if
 * it fires before the entry settles normally, it force-rejects the entry
 * with a sentinel and deletes it from the map. A follower observing that
 * sentinel gives up waiting and returns `undefined` from `onRequest` instead
 * of propagating a synthetic error — i.e. it falls through to make its own
 * real network call, exactly as if it had found no leader at all. A genuine
 * leader failure (a real HTTP error or a thrown network error) is NOT this
 * sentinel and still propagates to every follower as before.
 */
export function createDedupeGetMiddleware(safetyTimeoutMs = DEFAULT_SAFETY_TIMEOUT_MS): Middleware {
  const pending = new Map<string, PendingGet>()

  function keyFor(request: Request): string {
    return `${request.method} ${request.url}`
  }

  return {
    async onRequest({ request }) {
      if (request.method !== 'GET') {
        return undefined
      }

      const key = keyFor(request)
      const existing = pending.get(key)
      if (existing) {
        try {
          const finalResponse = await existing.promise
          return finalResponse.clone()
        } catch (error) {
          if (error === SAFETY_TIMEOUT) {
            // The leader's entry leaked (see doc comment above) — stop
            // waiting and make a real network call ourselves rather than
            // hang or fail synthetically.
            return undefined
          }
          throw error
        }
      }

      let resolve!: (response: Response) => void
      let reject!: (error: unknown) => void
      const promise = new Promise<Response>((res, rej) => {
        resolve = res
        reject = rej
      })
      // A solo GET (no follower ever attaches a handler) would otherwise
      // reject this promise with nobody listening, surfacing as an
      // `unhandledrejection`. This passive handler exists purely to prevent
      // that; it never affects what `.then`/`await`/`.catch` on `promise`
      // itself (used by real followers, and by the safety-timeout catch
      // above) observes.
      promise.catch(() => undefined)

      const timer = setTimeout(() => {
        // Compare by `reject` identity (rather than re-reading `entry`,
        // which isn't declared yet at this point in the closure) to confirm
        // this is still the SAME pending entry — it could have already
        // settled normally and been replaced by a new leader for this key.
        if (pending.get(key)?.reject === reject) {
          pending.delete(key)
          reject(SAFETY_TIMEOUT)
        }
      }, safetyTimeoutMs)

      // Synchronous — no await above this line — so a concurrent leader race
      // on the exact same key can't happen within a single JS turn.
      pending.set(key, { promise, resolve, reject, timer })
      return undefined
    },

    onResponse({ request, response }) {
      if (request.method !== 'GET') {
        return undefined
      }
      const key = keyFor(request)
      const entry = pending.get(key)
      // Only ever set for the leader's own call — a follower short-circuited
      // in `onRequest` above, which skips this hook entirely for that call.
      if (entry) {
        clearTimeout(entry.timer)
        pending.delete(key)
        entry.resolve(response.clone())
      }
      return undefined
    },

    onError({ request, error }) {
      if (request.method !== 'GET') {
        return undefined
      }
      const key = keyFor(request)
      const entry = pending.get(key)
      if (entry) {
        clearTimeout(entry.timer)
        pending.delete(key)
        entry.reject(error)
      }
      // Never handle/swallow the error — only propagate it to followers; the
      // leader's own call still throws exactly as it would have unmodified.
      return undefined
    },
  }
}

/** The single instance `client.ts` registers on `apiClient` — see the factory's doc comment for the full design. */
export const dedupeGetMiddleware = createDedupeGetMiddleware()
